/**
 * O ANFITRIÃO DO TOUR — sem tela. Decide QUANDO o tour abre e liga o motor.
 *
 * Abre em dois casos: alguém pediu (`/tour` sobe `pedidos`) ou é a primeira vez
 * DESTE usuário (progresso é do usuário, não da empresa: o convidado em base
 * existente também vê). O abrir-sozinho espera a tela assentar, só roda na
 * conversa e nunca atropela o episódio de boas-vindas do Axis numa base nova.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useAuthStore } from '@/shared/stores/auth'
import { useChatStore } from '@/stores/chatStore'
import { ITENS_DO_TOUR, useGuidedTourStore, type DesfechoDoTour } from '@/stores/guidedTourStore'
import { useOnboardingStatusStore } from '@/stores/onboardingStatusStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useReleaseKeysStore } from '@/stores/releaseKeysStore'
import { CHAVE_COMANDO_DE_VOZ } from '@/lib/release-keys'
import { isSpeechRecognitionSupported } from '@/lib/voice'
import { abrirTour, type TourAberto } from './motor-do-tour'
import { esperarElemento } from './esperar-elemento'
import { BARRA_LATERAL } from './preparar'
import { montarPassos } from './passos'
import type { RotaDoPasso, RotulosDoTour } from './types'

export const ASSENTAR_TELA_MS = 1500
/** Teto de espera pelas permissões: depois disso abre mesmo assim, sem os passos de equipe. */
export const ESPERA_MAXIMA_DAS_PERMISSOES_MS = 10000
/** Quanto esperar a tela do app aparecer antes de desistir de abrir (sem gravar nada). */
export const ESPERA_MAXIMA_DA_TELA_MS = 30000

export function podeGerirEquipe(): boolean {
  const { status, roleName, permissions } = usePermissionsStore.getState()
  if (status !== 'loaded') return false
  if (roleName === 'owner' || roleName === 'admin') return true
  return permissions.some((p) => p.resource === 'team' && p.action === 'read')
}

/** O microfone só existe com a chave de voz ligada e um reconhecedor no aparelho. */
function microfoneDisponivel(): boolean {
  return useReleaseKeysStore.getState().isReleased(CHAVE_COMANDO_DE_VOZ) && isSpeechRecognitionSupported()
}

const naConversa = (path: string) => path === '/chat' || path.startsWith('/chat/')

/** O campo da mensagem é controlado pelo React: limpar exige o setter nativo + evento. */
export function limparCampoDaMensagem(): void {
  const campo = document.querySelector<HTMLTextAreaElement>('[data-testid="chat-input"]')
  if (!campo) return
  const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
  setter?.call(campo, '')
  campo.dispatchEvent(new Event('input', { bubbles: true }))
}

/** Base nova: espera o Axis terminar a saudação (primeira resposta) antes de abrir. */
function onboardingDoMotorEmAndamento(): boolean {
  const { isNewTenant, loading } = useOnboardingStatusStore.getState()
  if (loading) return true
  if (!isNewTenant) return false
  return !useChatStore.getState().messages.some((m) => m.role === 'assistant')
}

function irParaRota(navigate: NavigateFunction, rota: RotaDoPasso): void {
  if (rota === 'equipe') {
    void navigate('/settings/team')
    return
  }
  const id = useChatStore.getState().activeConversationId
  void navigate(id ? `/chat/${id}` : '/chat')
}

/** Grava como o tour terminou. Quem foi até o fim já viu todas as funções: sem ponto pulsando nem item pendente. */
function registrarFechamento(desfecho: DesfechoDoTour): void {
  const tour = useGuidedTourStore.getState()
  tour.definirFechamento(null)
  tour.registrar(desfecho)
  if (desfecho === 'concluido') for (const item of ITENS_DO_TOUR) tour.marcarFeito(item)
  tour.marcarAtivo(false)
}

function rotulosDoTour(t: TFunction): RotulosDoTour {
  return {
    proximo: t('guidedTour.botoes.proximo'),
    voltar: t('guidedTour.botoes.voltar'),
    concluir: t('guidedTour.botoes.concluir'),
    progresso: t('guidedTour.botoes.progresso'),
    fechar: t('guidedTour.botoes.fechar'),
  }
}

export function GuidedTourHost(): null {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const userId = useAuthStore((s) => s.userId)
  const pedidos = useGuidedTourStore((s) => s.pedidos)
  const ativo = useGuidedTourStore((s) => s.ativo)
  const registro = useGuidedTourStore((s) => s.registro)
  const isNewTenant = useOnboardingStatusStore((s) => s.isNewTenant)
  const carregando = useOnboardingStatusStore((s) => s.loading)
  const permissoes = usePermissionsStore((s) => s.status)
  const haResposta = useChatStore((s) => s.messages.some((m) => m.role === 'assistant'))
  const pedidosVistos = useRef(pedidos)
  const aberto = useRef<TourAberto | null>(null)
  const rotaAtual = useRef(pathname)
  rotaAtual.current = pathname

  useEffect(() => {
    if (userId) useGuidedTourStore.getState().carregar(userId)
  }, [userId])

  const irPara = useCallback((rota: RotaDoPasso) => irParaRota(navigate, rota), [navigate])

  const abrir = useCallback(async () => {
    if (aberto.current || useGuidedTourStore.getState().ativo) return
    const store = useGuidedTourStore.getState()
    store.marcarAtivo(true)
    if (!naConversa(rotaAtual.current)) irPara('chat')
    // O tour só abre com o app de verdade na tela (a barra lateral existe no Hub e na conversa).
    // Medido no deploy: a rota já era /chat, mas o acesso ainda estava sendo verificado e o balão
    // apareceu por cima de uma tela preta. Exigir o campo de mensagem seria pior: quem entra pela
    // primeira vez cai no Hub, que não tem campo, e o tour nunca abriria.
    if (!(await esperarElemento(BARRA_LATERAL, ESPERA_MAXIMA_DA_TELA_MS))) {
      store.marcarAtivo(false)
      return
    }
    const passos = montarPassos(t, {
      podeGerirEquipe: podeGerirEquipe(),
      temMicrofone: microfoneDisponivel(),
      limparCampo: limparCampoDaMensagem,
    })
    aberto.current = abrirTour({
      passos,
      rotulos: rotulosDoTour(t),
      irPara,
      aoFechar: (desfecho) => {
        aberto.current = null
        registrarFechamento(desfecho)
      },
    })
    store.definirFechamento(aberto.current.fechar)
  }, [irPara, t])

  useEffect(() => {
    if (pedidos === pedidosVistos.current) return
    pedidosVistos.current = pedidos
    void abrir()
  }, [pedidos, abrir])

  useEffect(() => {
    if (!userId || ativo || registro !== null || !naConversa(pathname)) return
    if (carregando || (isNewTenant && !haResposta)) return
    // Permissões ainda chegando: abrir agora tiraria os passos de equipe de um admin e o tour
    // seria gravado como concluído sem eles. Espera assentar, com teto para não travar o tour.
    const esperaPermissoes = permissoes === 'idle' || permissoes === 'loading'
    const timer = window.setTimeout(
      () => {
        if (useGuidedTourStore.getState().deveAbrirSozinho() && !onboardingDoMotorEmAndamento())
          void abrir()
      },
      esperaPermissoes ? ESPERA_MAXIMA_DAS_PERMISSOES_MS : ASSENTAR_TELA_MS,
    )
    return () => window.clearTimeout(timer)
  }, [userId, ativo, registro, pathname, carregando, isNewTenant, haResposta, permissoes, abrir])

  useEffect(() => () => aberto.current?.fechar(), [])
  return null
}
